import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import type { SupplierPipelineStatus } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  cn,
} from '../design';
import { useCreateSupplier, useSuppliers } from '../lib/suppliers';

const ALL_STATUSES: SupplierPipelineStatus[] = [
  'lead',
  'contacted',
  'replied',
  'negotiating',
  'active',
  'passed',
];

const ACTIVE_STATUSES: SupplierPipelineStatus[] = [
  'lead',
  'contacted',
  'replied',
  'negotiating',
  'active',
];

const STATUS_LABELS: Record<SupplierPipelineStatus, string> = {
  lead: 'Lead',
  contacted: 'Contacted',
  replied: 'Replied',
  negotiating: 'Negotiating',
  active: 'Active',
  passed: 'Passed',
};

// `open` is a meta-filter (= every non-passed status). It exists alongside
// the literal `active` stage (= "currently buying from"), so we MUST NOT
// reuse `active` as the meta key — that collided with the stage's filter
// pill and produced a duplicate React key + a confusing duplicate label.
type FilterMode = 'open' | 'all' | SupplierPipelineStatus;

function statusBadgeVariant(status: SupplierPipelineStatus): 'neutral' | 'accent' | 'positive' {
  if (status === 'active') return 'positive';
  if (status === 'negotiating' || status === 'replied') return 'accent';
  return 'neutral';
}

export function SuppliersPage() {
  const [filter, setFilter] = useState<FilterMode>('open');
  const [creating, setCreating] = useState(false);

  const queryParams =
    filter === 'all'
      ? {}
      : filter === 'open'
        ? { status: ACTIVE_STATUSES.join(',') }
        : { status: filter };

  const { data: suppliers, isLoading, isError, error } = useSuppliers(queryParams);

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/search"
              className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast"
            >
              <ChevronLeft className="size-5" />
            </Link>
            <div>
              <h1 className="text-xl font-medium leading-tight">Suppliers</h1>
              <p className="text-xs text-fg-tertiary">
                Wholesale supplier pipeline. Discovery and outreach drafting ship in upcoming weeks.
              </p>
            </div>
          </div>
          <Dialog open={creating} onOpenChange={setCreating}>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              New supplier
            </Button>
            <DialogContent>
              <CreateSupplierForm onClose={() => setCreating(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-8 space-y-5">
        {/* Filter pills */}
        <div
          className="flex items-center gap-2 flex-wrap"
          role="tablist"
          aria-label="Pipeline filter"
        >
          {(
            [
              { key: 'open' as FilterMode, label: 'Open' },
              ...ALL_STATUSES.map((s) => ({ key: s as FilterMode, label: STATUS_LABELS[s] })),
              { key: 'all' as FilterMode, label: 'All' },
            ] satisfies Array<{ key: FilterMode; label: string }>
          ).map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors duration-fast',
                filter === f.key
                  ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-fg'
                  : 'border-border-subtle text-fg-secondary hover:text-fg-primary hover:border-border-default'
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-fg-secondary">Loading…</p>}
        {isError && (
          <Card className="border-negative/30 bg-negative/[0.06]">
            <p className="text-sm text-negative">
              Failed to load suppliers: {(error as Error).message ?? 'Unknown error'}
            </p>
          </Card>
        )}

        {suppliers && suppliers.length === 0 && (
          <Card className="text-center py-14">
            <h2 className="text-base font-medium mb-2">No suppliers yet</h2>
            <p className="text-sm text-fg-secondary mb-1 max-w-md mx-auto">
              Pin a supplier to one of your products to start the pipeline. Discovery (auto-finding
              suppliers from an ASIN) ships in Week 15.
            </p>
            <p className="text-xs text-fg-tertiary mb-5">
              For now, find a product in{' '}
              <Link to="/products" className="text-accent-fg hover:underline">
                your products list
              </Link>{' '}
              and pin a supplier from the detail page — or create one manually.
            </p>
            <Button leadingIcon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Create supplier manually
            </Button>
          </Card>
        )}

        {suppliers && suppliers.length > 0 && (
          <div className="border border-border-subtle rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-1 text-xs text-fg-tertiary">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Supplier</th>
                  <th className="text-left font-medium px-4 py-2.5">Status</th>
                  <th className="text-right font-medium px-4 py-2.5 tabular-nums">ASINs</th>
                  <th className="text-left font-medium px-4 py-2.5">Last activity</th>
                  <th className="px-2" />
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-border-subtle hover:bg-surface-1/60 transition-colors duration-fast"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{s.name}</div>
                      {s.website && <div className="text-xs text-fg-tertiary">{s.website}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant={statusBadgeVariant(s.status)}>
                        {STATUS_LABELS[s.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{s.pinnedAsinCount}</td>
                    <td className="px-4 py-2.5 text-fg-secondary text-xs">
                      {new Date(s.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-2.5 text-right">
                      <Link
                        to="/suppliers/$id"
                        params={{ id: s.id }}
                        className="text-fg-tertiary hover:text-fg-primary transition-colors duration-fast inline-flex items-center"
                        aria-label={`Open ${s.name}`}
                      >
                        <ChevronRight className="size-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function CreateSupplierForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const create = useCreateSupplier();

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        try {
          await create.mutateAsync({
            name: name.trim(),
            website: website.trim() || null,
            primaryEmail: primaryEmail.trim() || null,
            status: 'lead',
          });
          onClose();
        } catch {
          // surfaced via create.error
        }
      }}
      className="space-y-4"
    >
      <DialogHeader>
        <DialogTitle>New supplier</DialogTitle>
        <DialogDescription>
          Quick add — you can fill in more details on the supplier detail page.
        </DialogDescription>
      </DialogHeader>

      <FormField label="Name" htmlFor="name" required>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Anker Innovations"
          autoFocus
        />
      </FormField>

      <FormField label="Website" htmlFor="website">
        <Input
          id="website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="anker.com"
        />
      </FormField>

      <FormField label="Primary email" htmlFor="email">
        <Input
          id="email"
          type="email"
          value={primaryEmail}
          onChange={(e) => setPrimaryEmail(e.target.value)}
          placeholder="wholesale@anker.com"
        />
      </FormField>

      {create.isError && (
        <div className="rounded-sm bg-negative/[0.12] border border-negative/30 px-3 py-2">
          <p className="text-sm text-negative">{(create.error as Error).message}</p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          Create
        </Button>
      </DialogFooter>
    </form>
  );
}
